import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { CompanyBoardItem } from '../../services/company-api.js';
import { submitStrategyClarification, reviseStrategy, approveStrategy, answerRoutineQuestions, archiveStrategy, retryStrategy, cancelStrategy, toggleRoutine } from '../../services/company-api.js';
import { respondToTicket, approveToolRequest, approveContentPreview, deleteTicket, parseContentPreviewMeta, driveUrlToThumbnail } from '../../services/founder-board-api.js';
import type { FounderTicketStatus } from '../../services/founder-board-api.js';

const TYPE_ICONS: Record<string, string> = {
  strategy: 'S',
  task: 'T',
  founder_ticket: 'F',
  specwright: 'P',
  routine: 'R',
};

const TYPE_LABELS: Record<string, string> = {
  strategy: 'Strategie',
  task: 'Aufgabe',
  founder_ticket: 'Ticket',
  specwright: 'Spec',
  routine: 'Routine',
};

const STATUS_LABELS: Record<string, string> = {
  goal_received: 'Ziel erhalten',
  clarifying: 'Rueckfragen',
  ready: 'Bereit',
  strategizing: 'Strategieentwicklung',
  strategy_review: 'Review',
  executing: 'Ausfuehrung',
  blocked: 'Blockiert',
  reporting: 'Bericht',
  completed: 'Abgeschlossen',
  failed: 'Fehlgeschlagen',
  open: 'Offen',
  waiting: 'Wartend',
  approved: 'Genehmigt',
  done: 'Erledigt',
  'not-started': 'Nicht gestartet',
  'in-progress': 'In Arbeit',
  complete: 'Abgeschlossen',
  active: 'Aktiv',
  paused: 'Pausiert',
  needs_input: 'Rueckfrage',
};

@customElement('cp-company-board-card')
export class CpCompanyBoardCard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) item!: CompanyBoardItem;
  @property({ type: Array }) subItems: CompanyBoardItem[] = [];
  @property({ type: String }) parentStrategyTitle = '';
  @property({ type: Number }) delegatedTaskCount = 0;
  @property({ type: Number }) delegatedTaskDone = 0;

  @state() private _expanded = false;
  @state() private _fullscreen = false;
  @state() private _clarifyAnswers: Record<number, string> = {};
  @state() private _revisionFeedback = '';
  @state() private _submitting = false;
  @state() private _routineAnswers: Record<number, string> = {};
  @state() private _ticketReply = '';
  @state() private _showTicketReply = false;
  @state() private _contentCaptionEdit = '';
  @state() private _showContentCaption = false;
  @state() private _showPrompt = false;

  private _renderMarkdown(content: string) {
    const rawHtml = marked.parse(content, { async: false }) as string;
    const sanitized = DOMPurify.sanitize(rawHtml);
    return html`${unsafeHTML(sanitized)}`;
  }

  private _handleClick() {
    this._expanded = !this._expanded;
    this.dispatchEvent(new CustomEvent('board-card-click', {
      detail: { item: this.item },
      bubbles: true,
      composed: true,
    }));
  }

  private _toggleFullscreen(e: Event) {
    e.stopPropagation();
    this._fullscreen = !this._fullscreen;
    if (this._fullscreen) {
      this._expanded = true;
    }
  }

  private _handleOverlayClick(e: Event) {
    if ((e.target as HTMLElement).classList.contains('cp-board-card-fullscreen__overlay')) {
      this._fullscreen = false;
    }
  }

  private _handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this._fullscreen) {
      this._fullscreen = false;
    }
  };

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._handleKeydown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._handleKeydown);
  }

  private _handleAnswerInput(index: number, e: Event) {
    const input = e.target as HTMLTextAreaElement;
    this._clarifyAnswers = { ...this._clarifyAnswers, [index]: input.value };
  }

  private async _handleSubmitClarification(e: Event) {
    e.stopPropagation();
    const strategyId = this.item.metadata?.strategyId as number | undefined;
    if (!strategyId) return;

    const { unanswered } = this._splitQuestions();
    if (!unanswered.length) return;

    // Build new answers for the unanswered questions only
    const newParts = unanswered.map((q, i) =>
      `**${q}**\n${this._clarifyAnswers[i] ?? '(keine Antwort)'}`
    );
    const newAnswers = newParts.join('\n\n');

    // Append to existing answers
    const existingAnswers = (this.item.metadata?.clarificationAnswers as string | null) ?? '';
    const combined = existingAnswers
      ? `${existingAnswers}\n\n${newAnswers}`
      : newAnswers;

    this._submitting = true;
    try {
      await submitStrategyClarification(strategyId as number, combined);
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Clarification submit failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private _parseClarifyingQuestions(): string[] {
    const raw = this.item.metadata?.clarifyingQuestions as string | null;
    if (!raw) return [];
    try {
      return JSON.parse(raw) as string[];
    } catch {
      return [];
    }
  }

  /** Split questions into already-answered (from previous rounds) and new unanswered ones. */
  private _splitQuestions(): { answered: Array<{ question: string; answer: string }>; unanswered: string[] } {
    const allQuestions = this._parseClarifyingQuestions();
    const answersRaw = (this.item.metadata?.clarificationAnswers as string | null) ?? '';

    // Count answered questions by counting **...** blocks in the answers string
    const answeredCount = answersRaw ? (answersRaw.match(/\*\*[^*]+\*\*/g) ?? []).length : 0;

    const answered: Array<{ question: string; answer: string }> = [];
    // Parse individual answers from the raw string
    const answerBlocks = answersRaw ? answersRaw.split(/\n\n(?=\*\*)/).filter(Boolean) : [];
    for (let i = 0; i < answeredCount && i < allQuestions.length; i++) {
      const block = answerBlocks[i] ?? '';
      const answerText = block.replace(/^\*\*[^*]+\*\*\n?/, '').trim();
      answered.push({ question: allQuestions[i], answer: answerText });
    }

    const unanswered = allQuestions.slice(answeredCount);
    return { answered, unanswered };
  }

  private _renderClarification() {
    const { answered, unanswered } = this._splitQuestions();
    if (!answered.length && !unanswered.length) return nothing;

    const allNew = unanswered.every((_, i) => (this._clarifyAnswers[i] ?? '').trim().length > 0);

    return html`
      <div class="cp-board-card__clarification">
        ${answered.length > 0 ? html`
          <p class="cp-board-card__detail-label">Bereits geklaert</p>
          ${answered.map((qa, i) => html`
            <div class="cp-board-card__clarify-item cp-board-card__clarify-item--answered">
              <p class="cp-board-card__clarify-question">${i + 1}. ${qa.question}</p>
              <p class="cp-board-card__clarify-answer">${qa.answer}</p>
            </div>
          `)}
        ` : nothing}

        ${unanswered.length > 0 ? html`
          <p class="cp-board-card__detail-label">${answered.length > 0 ? 'Neue Rueckfragen' : 'Rueckfragen des CEO'}</p>
          ${unanswered.map((q, i) => html`
            <div class="cp-board-card__clarify-item">
              <p class="cp-board-card__clarify-question">${answered.length + i + 1}. ${q}</p>
              <textarea
                class="cp-board-card__clarify-input"
                placeholder="Deine Antwort..."
                .value=${this._clarifyAnswers[i] ?? ''}
                @input=${(e: Event) => this._handleAnswerInput(i, e)}
                @click=${(e: Event) => e.stopPropagation()}
                rows="2"
              ></textarea>
            </div>
          `)}
          <button
            class="cp-board-card__clarify-submit"
            ?disabled=${!allNew || this._submitting}
            @click=${this._handleSubmitClarification}
          >
            ${this._submitting ? 'Wird gesendet...' : 'Antworten absenden'}
          </button>
        ` : nothing}
      </div>
    `;
  }

  private async _handleTicketRespond(newStatus: FounderTicketStatus) {
    const ticketId = this.item.metadata?.ticketId as number | undefined;
    if (!ticketId || !this._ticketReply.trim()) return;

    this._submitting = true;
    try {
      await respondToTicket(ticketId, this._ticketReply.trim(), newStatus);
      this._ticketReply = '';
      this._showTicketReply = false;
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Ticket respond failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private async _handleTicketDismiss() {
    const ticketId = this.item.metadata?.ticketId as number | undefined;
    if (!ticketId) return;

    this._submitting = true;
    try {
      await respondToTicket(ticketId, 'Thema verworfen – Gruender hat das Ticket geschlossen.', 'done');
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Ticket dismiss failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private async _handleTicketDelete() {
    const ticketId = this.item.metadata?.ticketId as number | undefined;
    if (!ticketId) return;

    this._submitting = true;
    try {
      await deleteTicket(ticketId);
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Ticket delete failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private async _handleTicketApproveContent(editedCaption?: string) {
    const ticketId = this.item.metadata?.ticketId as number | undefined;
    if (!ticketId) return;

    this._submitting = true;
    try {
      await approveContentPreview(ticketId, editedCaption);
      this._showContentCaption = false;
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Content approve failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private async _handleTicketApproveTool() {
    const ticketId = this.item.metadata?.ticketId as number | undefined;
    if (!ticketId) return;

    this._submitting = true;
    try {
      await approveToolRequest(ticketId);
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Tool approve failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private _renderFounderTicketDetail() {
    const { item } = this;
    const ticketType = item.metadata?.ticketType as string | null;
    const founderComment = item.metadata?.founderComment as string | null;
    const isToolRequest = ticketType === 'tool_request';
    const isContentPreview = ticketType === 'content_preview';
    const contentMeta = isContentPreview && item.description ? parseContentPreviewMeta(item.description) : null;
    const isOpen = item.status === 'open';

    return html`
      ${contentMeta ? html`
        <div class="cp-board-card__detail-section">
          ${contentMeta.image_urls[0] ? html`
            <img src="${driveUrlToThumbnail(contentMeta.image_urls[0])}" alt="Preview" style="width:100%;border-radius:var(--radius-md);margin-bottom:var(--spacing-sm);" loading="lazy" />
          ` : nothing}
          <p class="cp-board-card__detail-label">Caption</p>
          <div class="cp-board-card__detail-content" style="white-space:pre-wrap;">${contentMeta.caption}</div>
          ${contentMeta.hashtags.length > 0 ? html`
            <div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:var(--spacing-sm);">
              ${contentMeta.hashtags.map(tag => html`
                <span style="padding:2px 8px;border-radius:99px;font-size:11px;background:rgba(0,212,255,0.1);color:var(--color-accent-primary);">${tag.startsWith('#') ? tag : `#${tag}`}</span>
              `)}
            </div>
          ` : nothing}
        </div>
      ` : item.description ? html`
        <div class="cp-board-card__detail-section">
          <p class="cp-board-card__detail-label">Beschreibung</p>
          <div class="cp-board-card__detail-content cp-board-card__markdown">${this._renderMarkdown(item.description)}</div>
        </div>
      ` : nothing}

      ${founderComment ? html`
        <div class="cp-board-card__detail-section">
          <p class="cp-board-card__detail-label">Meine Antwort</p>
          <div class="cp-board-card__detail-content">${founderComment}</div>
        </div>
      ` : nothing}

      ${isOpen ? html`
        <div class="cp-board-card__revision">
          ${isToolRequest ? html`
            <button
              class="cp-board-card__approve-btn"
              ?disabled=${this._submitting}
              @click=${(e: Event) => { e.stopPropagation(); void this._handleTicketApproveTool(); }}
            >
              ${this._submitting ? 'Wird gesendet...' : 'Tool genehmigen'}
            </button>
          ` : nothing}

          ${isContentPreview && !this._showContentCaption ? html`
            <div style="display: flex; gap: 8px; margin-bottom: 8px;">
              <button
                class="cp-board-card__approve-btn"
                style="background: rgba(34,197,94,0.9);"
                ?disabled=${this._submitting}
                @click=${(e: Event) => { e.stopPropagation(); void this._handleTicketApproveContent(); }}
              >
                ${this._submitting ? '...' : 'Genehmigen & Posten'}
              </button>
              <button
                class="cp-board-card__clarify-submit"
                @click=${(e: Event) => { e.stopPropagation(); this._showContentCaption = true; this._contentCaptionEdit = contentMeta?.caption ?? ''; }}
              >
                Caption anpassen
              </button>
            </div>
          ` : nothing}

          ${this._showContentCaption ? html`
            <textarea
              class="cp-board-card__clarify-input"
              .value=${this._contentCaptionEdit}
              @input=${(e: Event) => { this._contentCaptionEdit = (e.target as HTMLTextAreaElement).value; }}
              @click=${(e: Event) => e.stopPropagation()}
              rows="4"
            ></textarea>
            <div style="display: flex; gap: 8px; margin-top: 8px;">
              <button
                class="cp-board-card__approve-btn"
                style="background: rgba(34,197,94,0.9);"
                ?disabled=${this._submitting || !this._contentCaptionEdit.trim()}
                @click=${(e: Event) => { e.stopPropagation(); void this._handleTicketApproveContent(this._contentCaptionEdit); }}
              >
                ${this._submitting ? '...' : 'Mit neuer Caption genehmigen'}
              </button>
              <button
                class="cp-board-card__clarify-submit"
                style="background: var(--color-text-muted, #6b7280); color: white;"
                @click=${(e: Event) => { e.stopPropagation(); this._showContentCaption = false; }}
              >
                Abbrechen
              </button>
            </div>
          ` : nothing}

          ${!this._showTicketReply ? html`
            <div style="display: flex; gap: 8px;">
              <button
                class="cp-board-card__clarify-submit"
                @click=${(e: Event) => { e.stopPropagation(); this._showTicketReply = true; }}
              >
                Antworten
              </button>
              <button
                class="cp-board-card__clarify-submit"
                style="background: var(--color-text-muted, #6b7280); color: white;"
                ?disabled=${this._submitting}
                @click=${(e: Event) => { e.stopPropagation(); void this._handleTicketDismiss(); }}
              >
                ${this._submitting ? '...' : 'Schliessen'}
              </button>
              <button
                class="cp-board-card__clarify-submit"
                style="background: var(--color-danger, #ef4444); color: white;"
                ?disabled=${this._submitting}
                @click=${(e: Event) => { e.stopPropagation(); void this._handleTicketDelete(); }}
              >
                ${this._submitting ? '...' : 'Loeschen'}
              </button>
            </div>
          ` : html`
            <textarea
              class="cp-board-card__clarify-input"
              placeholder="Kommentar / Feedback..."
              .value=${this._ticketReply}
              @input=${(e: Event) => { this._ticketReply = (e.target as HTMLTextAreaElement).value; }}
              @click=${(e: Event) => e.stopPropagation()}
              rows="3"
            ></textarea>
            <div class="cp-board-card__actions" style="display: flex; gap: 8px; margin-top: 8px;">
              <button
                class="cp-board-card__approve-btn"
                ?disabled=${!this._ticketReply.trim() || this._submitting}
                @click=${(e: Event) => { e.stopPropagation(); void this._handleTicketRespond('approved'); }}
              >
                Genehmigen
              </button>
              <button
                class="cp-board-card__clarify-submit cp-board-card__revision-btn"
                ?disabled=${!this._ticketReply.trim() || this._submitting}
                @click=${(e: Event) => { e.stopPropagation(); void this._handleTicketRespond('waiting'); }}
              >
                Rueckfrage an CEO
              </button>
              <button
                class="cp-board-card__clarify-submit"
                style="background: var(--color-text-muted, #6b7280); color: white;"
                @click=${(e: Event) => { e.stopPropagation(); this._showTicketReply = false; this._ticketReply = ''; }}
              >
                Abbrechen
              </button>
            </div>
          `}
        </div>
      ` : html`
        <div class="cp-board-card__revision" style="margin-top: 8px;">
          <button
            class="cp-board-card__clarify-submit"
            style="background: var(--color-danger, #ef4444); color: white;"
            ?disabled=${this._submitting}
            @click=${(e: Event) => { e.stopPropagation(); void this._handleTicketDelete(); }}
          >
            ${this._submitting ? '...' : 'Ticket loeschen'}
          </button>
        </div>
      `}
    `;
  }

  private _renderDetail() {
    const { item } = this;
    const report = item.metadata?.executionReport as string | null;
    const strategyContent = item.metadata?.strategyContent as string | null;
    const blockReason = item.metadata?.blockReason as string | null;
    const description = item.description;

    // Clarifying state: show questions form
    const isClarifying = item.status === 'clarifying';
    if (isClarifying) {
      return this._renderClarification();
    }

    // Founder ticket type: show description + response actions
    if (item.type === 'founder_ticket') {
      return this._renderFounderTicketDetail();
    }

    // Routine type: show schedule, last run, and pending questions
    if (item.type === 'routine') {
      return this._renderRoutineDetail();
    }

    // Determine what to show based on state
    const isFailed = item.status === 'failed';
    const isCompleted = item.status === 'completed' || item.status === 'reporting';
    const isBlocked = item.status === 'blocked';

    const sections: Array<{ label: string; content: string }> = [];

    if (isBlocked && blockReason) {
      sections.push({ label: 'Blockiert', content: blockReason });
    }

    if (isFailed && report) {
      sections.push({ label: 'Fehlerbericht', content: report });
    } else if (isFailed && blockReason) {
      sections.push({ label: 'Fehlergrund', content: blockReason });
    }

    if (isCompleted && report) {
      sections.push({ label: 'Ergebnis', content: report });
    }

    if (strategyContent && !isFailed) {
      sections.push({ label: 'Strategie', content: strategyContent });
    }

    if (description && sections.length === 0) {
      sections.push({ label: 'Beschreibung', content: description });
    }

    if (sections.length === 0) {
      return html`<div class="cp-board-card__detail-empty">Keine Details verfuegbar</div>`;
    }

    const isReview = item.status === 'strategy_review';

    return html`
      ${sections.map(s => html`
        <div class="cp-board-card__detail-section">
          <p class="cp-board-card__detail-label">${s.label}</p>
          <div class="cp-board-card__detail-content cp-board-card__markdown">${this._renderMarkdown(s.content)}</div>
        </div>
      `)}
      ${isFailed ? html`
        <div class="cp-board-card__revision" style="display: flex; gap: 8px; margin-top: 8px;">
          <button
            class="cp-board-card__clarify-submit"
            ?disabled=${this._submitting}
            @click=${this._handleArchive}
          >
            ${this._submitting ? '...' : 'Archivieren'}
          </button>
          <button
            class="cp-board-card__approve-btn"
            ?disabled=${this._submitting}
            @click=${this._handleRetry}
          >
            ${this._submitting ? '...' : 'Nochmal versuchen'}
          </button>
        </div>
      ` : nothing}
      ${!isFailed && !isCompleted && !isReview && item.type === 'strategy' ? html`
        <div class="cp-board-card__revision" style="margin-top: 8px;">
          <button
            class="cp-board-card__clarify-submit"
            style="background: var(--color-text-muted, #6b7280); color: white;"
            ?disabled=${this._submitting}
            @click=${this._handleCancel}
          >
            ${this._submitting ? '...' : 'Strategie abbrechen'}
          </button>
        </div>
      ` : nothing}
      ${isReview ? html`
        <div class="cp-board-card__revision">
          <button
            class="cp-board-card__approve-btn"
            ?disabled=${this._submitting}
            @click=${this._handleApprove}
          >
            ${this._submitting ? 'Wird gesendet...' : 'Strategie genehmigen & starten'}
          </button>
          <div class="cp-board-card__revision-divider">oder Feedback geben:</div>
          <textarea
            class="cp-board-card__clarify-input"
            placeholder="Feedback / Antwort an den CEO..."
            .value=${this._revisionFeedback}
            @input=${(e: Event) => { this._revisionFeedback = (e.target as HTMLTextAreaElement).value; }}
            @click=${(e: Event) => e.stopPropagation()}
            rows="3"
          ></textarea>
          <button
            class="cp-board-card__clarify-submit cp-board-card__revision-btn"
            ?disabled=${!this._revisionFeedback.trim() || this._submitting}
            @click=${this._handleRevision}
          >
            ${this._submitting ? 'Wird gesendet...' : 'Ueberarbeiten lassen'}
          </button>
        </div>
      ` : nothing}
    `;
  }

  private async _handleRevision(e: Event) {
    e.stopPropagation();
    const strategyId = this.item.metadata?.strategyId as number | undefined;
    if (!strategyId || !this._revisionFeedback.trim()) return;

    this._submitting = true;
    try {
      await reviseStrategy(strategyId, this._revisionFeedback.trim());
      this._revisionFeedback = '';
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Revision submit failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private async _handleApprove(e: Event) {
    e.stopPropagation();
    const strategyId = this.item.metadata?.strategyId as number | undefined;
    if (!strategyId) return;

    this._submitting = true;
    try {
      await approveStrategy(strategyId);
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Approve failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private async _handleCancel(e: Event) {
    e.stopPropagation();
    const strategyId = this.item.metadata?.strategyId as number | undefined;
    if (!strategyId) return;

    this._submitting = true;
    try {
      await cancelStrategy(strategyId);
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Cancel failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private async _handleArchive(e: Event) {
    e.stopPropagation();
    const strategyId = this.item.metadata?.strategyId as number | undefined;
    if (!strategyId) return;

    this._submitting = true;
    try {
      await archiveStrategy(strategyId);
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Archive failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private async _handleRetry(e: Event) {
    e.stopPropagation();
    const strategyId = this.item.metadata?.strategyId as number | undefined;
    if (!strategyId) return;

    this._submitting = true;
    try {
      await retryStrategy(strategyId);
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Retry failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private async _handleToggleRoutine(e: Event) {
    e.stopPropagation();
    const cronJobId = this.item.metadata?.cronJobId as number | undefined;
    if (!cronJobId) return;

    this._submitting = true;
    try {
      this.dispatchEvent(new CustomEvent('routine-toggle', {
        detail: { cronJobId },
        bubbles: true,
        composed: true,
      }));
    } finally {
      this._submitting = false;
    }
  }

  private _renderRoutineDetail() {
    const { item } = this;
    const schedule = item.metadata?.schedule as string | null;
    const lastRun = item.metadata?.lastRun as string | null;
    const prompt = item.metadata?.prompt as string | null;
    const isEnabled = item.metadata?.enabled as boolean;
    const pendingQuestionsRaw = item.metadata?.pendingQuestions as string | null;

    let questions: string[] = [];
    if (pendingQuestionsRaw) {
      try { questions = JSON.parse(pendingQuestionsRaw) as string[]; } catch { /* ignore */ }
    }

    const hasQuestions = questions.length > 0;
    const allAnswered = hasQuestions && questions.every((_, i) => (this._routineAnswers[i] ?? '').trim().length > 0);

    return html`
      <div class="cp-board-card__detail-section">
        ${schedule ? html`<p class="cp-board-card__detail-label">Zeitplan: ${schedule}</p>` : nothing}
        ${lastRun ? html`<p class="cp-board-card__date">Letzter Lauf: ${new Date(lastRun).toLocaleString('de-DE')}</p>` : nothing}
      </div>

      <div class="cp-board-card__routine-toggle">
        <button
          class="cp-board-card__routine-toggle-btn ${isEnabled ? 'cp-board-card__routine-toggle-btn--active' : ''}"
          ?disabled=${this._submitting}
          @click=${this._handleToggleRoutine}
        >
          <span class="cp-board-card__routine-toggle-track">
            <span class="cp-board-card__routine-toggle-thumb"></span>
          </span>
          <span class="cp-board-card__routine-toggle-label">${isEnabled ? 'Aktiv' : 'Pausiert'}</span>
        </button>
      </div>

      ${hasQuestions ? html`
        <div class="cp-board-card__clarification">
          <p class="cp-board-card__detail-label">Rueckfragen</p>
          ${questions.map((q, i) => html`
            <div class="cp-board-card__clarify-item">
              <p class="cp-board-card__clarify-question">${i + 1}. ${q}</p>
              <textarea
                class="cp-board-card__clarify-input"
                placeholder="Deine Antwort..."
                .value=${this._routineAnswers[i] ?? ''}
                @input=${(e: Event) => { this._routineAnswers = { ...this._routineAnswers, [i]: (e.target as HTMLTextAreaElement).value }; }}
                @click=${(e: Event) => e.stopPropagation()}
                rows="2"
              ></textarea>
            </div>
          `)}
          <button
            class="cp-board-card__clarify-submit"
            ?disabled=${!allAnswered || this._submitting}
            @click=${this._handleSubmitRoutineAnswer}
          >
            ${this._submitting ? 'Wird gesendet...' : 'Antworten absenden'}
          </button>
        </div>
      ` : html`
        ${item.description ? html`
          <div class="cp-board-card__detail-section">
            <p class="cp-board-card__detail-label">Beschreibung</p>
            <div class="cp-board-card__detail-content">${item.description}</div>
          </div>
        ` : nothing}
      `}
      ${prompt ? html`
        <div class="cp-board-card__detail-section">
          <button
            class="cp-board-card__prompt-toggle"
            @click=${(e: Event) => { e.stopPropagation(); this._showPrompt = !this._showPrompt; }}
          >
            ${this._showPrompt ? '▾' : '▸'} Prompt anzeigen
          </button>
          ${this._showPrompt ? html`
            <div class="cp-board-card__detail-content cp-board-card__markdown cp-board-card__prompt-content">
              ${this._renderMarkdown(prompt)}
            </div>
          ` : nothing}
        </div>
      ` : nothing}
    `;
  }

  private async _handleSubmitRoutineAnswer(e: Event) {
    e.stopPropagation();
    const cronJobId = this.item.metadata?.cronJobId as number | undefined;
    if (!cronJobId) return;

    // Extract companyId from the item id (routine:123) or from parent
    const companyIdStr = this.item.id.split(':')[1];
    // We need the companyId - get it from the parent board via event
    const pendingQuestionsRaw = this.item.metadata?.pendingQuestions as string | null;
    let questions: string[] = [];
    if (pendingQuestionsRaw) {
      try { questions = JSON.parse(pendingQuestionsRaw) as string[]; } catch { /* ignore */ }
    }

    const parts = questions.map((q, i) => `**${q}**\n${this._routineAnswers[i] ?? '(keine Antwort)'}`);
    const answersText = parts.join('\n\n');

    this._submitting = true;
    try {
      // Dispatch event to let the board handle the API call (it knows the companyId)
      this.dispatchEvent(new CustomEvent('routine-answer-submit', {
        detail: { cronJobId, answers: answersText },
        bubbles: true,
        composed: true,
      }));
      this._routineAnswers = {};
      this.dispatchEvent(new CustomEvent('board-card-updated', {
        bubbles: true,
        composed: true,
      }));
    } catch (err) {
      console.error('Routine answer submit failed:', err);
    } finally {
      this._submitting = false;
    }
  }

  private _renderSubItems() {
    if (this.subItems.length === 0) return nothing;

    const done = this.subItems.filter(s => s.status === 'completed' || s.status === 'reporting').length;
    const total = this.subItems.length;

    return html`
      <div class="cp-board-card__sub-items">
        <div class="cp-board-card__sub-items-header">
          <span class="cp-board-card__detail-label">Teilaufgaben</span>
          <span class="cp-board-card__sub-items-progress">${done}/${total}</span>
        </div>
        ${this.subItems.map(sub => {
          const isDone = sub.status === 'completed' || sub.status === 'reporting';
          const isBlocked = sub.status === 'blocked' || sub.status === 'failed';
          const statusLabel = STATUS_LABELS[sub.status] ?? sub.status;
          return html`
            <div class="cp-board-card__sub-item ${isDone ? 'cp-board-card__sub-item--done' : ''} ${isBlocked ? 'cp-board-card__sub-item--blocked' : ''}">
              <span class="cp-board-card__sub-item-indicator ${isDone ? 'cp-board-card__sub-item-indicator--done' : ''} ${isBlocked ? 'cp-board-card__sub-item-indicator--blocked' : ''}"></span>
              <span class="cp-board-card__sub-item-title">${sub.title}</span>
              <span class="cp-board-card__sub-item-status">${statusLabel}</span>
            </div>
          `;
        })}
      </div>
    `;
  }

  private _formatRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMin / 60);
    const diffD = Math.floor(diffH / 24);

    if (diffMin < 1) return 'gerade eben';
    if (diffMin < 60) return `vor ${diffMin} Min.`;
    if (diffH < 24) return `vor ${diffH} Std.`;
    if (diffD < 7) return `vor ${diffD} T.`;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }

  private _renderCard(isFullscreen: boolean) {
    const { item } = this;
    const icon = TYPE_ICONS[item.type] ?? '?';
    const statusLabel = STATUS_LABELS[item.status] ?? item.status;
    const blockReason = item.metadata?.blockReason as string | null;
    const isBlocked = item.column === 'blocked';
    const isDone = item.column === 'done';

    const createdLabel = this._formatRelativeTime(item.createdAt);
    const updatedLabel = this._formatRelativeTime(item.updatedAt);
    const showUpdated = item.updatedAt !== item.createdAt;

    const isStrategy = item.type === 'strategy';
    const isTask = item.type === 'task';
    const hasDelegatedTasks = isStrategy && this.delegatedTaskCount > 0;

    return html`
      <div class="cp-board-card ${this._expanded ? 'cp-board-card--expanded' : ''} ${isFullscreen ? 'cp-board-card--fullscreen-card' : ''} ${isStrategy ? 'cp-board-card--strategy' : ''} ${isTask ? 'cp-board-card--task' : ''}" @click=${isFullscreen ? nothing : this._handleClick} title="${TYPE_LABELS[item.type] ?? item.type}: ${item.title}">
        <div class="cp-board-card__header">
          <span class="cp-board-card__type-icon cp-board-card__type-icon--${item.type}">
            ${icon}
          </span>
          <p class="cp-board-card__title">${item.title}</p>
          ${this._expanded && !isFullscreen ? html`
            <button class="cp-board-card__fullscreen-btn" @click=${this._toggleFullscreen} title="Vollbild">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
              </svg>
            </button>
          ` : nothing}
          ${isFullscreen ? html`
            <button class="cp-board-card__fullscreen-btn cp-board-card__close-btn" @click=${this._toggleFullscreen} title="Schliessen">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          ` : nothing}
          ${!isFullscreen ? html`
            <span class="cp-board-card__expand-icon">${this._expanded ? '\u25B2' : '\u25BC'}</span>
            ${item.type === 'founder_ticket' && item.status !== 'done' ? html`
              <button class="cp-board-card__close-btn" title="Ticket schliessen" @click=${() => this._handleTicketDismiss()}>
                &#10006;
              </button>
            ` : nothing}
          ` : nothing}
        </div>

        ${!this._expanded && blockReason ? html`
          <div class="cp-board-card__block-reason">${blockReason}</div>
        ` : nothing}

        <div class="cp-board-card__footer">
          ${item.assignedAgent ? html`
            <div class="cp-board-card__agent">
              <span class="cp-board-card__agent-dot" style="background-color: #6366f1">
                ${item.assignedAgent.name.charAt(0)}
              </span>
              <span class="cp-board-card__agent-name">${item.assignedAgent.name}</span>
            </div>
          ` : html`<span></span>`}

          <span class="cp-board-card__status ${isBlocked ? 'cp-board-card__status--blocked' : ''} ${isDone ? 'cp-board-card__status--done' : ''}">
            ${statusLabel}
          </span>
        </div>

        <div class="cp-board-card__timestamps">
          <span title="${new Date(item.createdAt).toLocaleString('de-DE')}">Erstellt ${createdLabel}</span>
          ${showUpdated ? html`
            <span title="${new Date(item.updatedAt).toLocaleString('de-DE')}">· Aktualisiert ${updatedLabel}</span>
          ` : nothing}
        </div>

        ${hasDelegatedTasks && !this._expanded ? html`
          <div class="cp-board-card__delegated-progress">
            <div class="cp-board-card__delegated-bar">
              <div class="cp-board-card__delegated-bar-fill" style="width: ${Math.round((this.delegatedTaskDone / this.delegatedTaskCount) * 100)}%"></div>
            </div>
            <span class="cp-board-card__delegated-label">${this.delegatedTaskDone}/${this.delegatedTaskCount} Tasks</span>
          </div>
        ` : nothing}

        ${isTask && this.parentStrategyTitle ? html`
          <div class="cp-board-card__parent-link">
            <span class="cp-board-card__parent-arrow">\u2190</span>
            <span class="cp-board-card__parent-name">${this.parentStrategyTitle}</span>
          </div>
        ` : nothing}

        ${this.subItems.length > 0 && !this._expanded ? html`
          <div class="cp-board-card__sub-items-compact">
            ${(() => {
              const done = this.subItems.filter(s => s.status === 'completed' || s.status === 'reporting').length;
              return html`<span class="cp-board-card__sub-items-badge">${done}/${this.subItems.length} erledigt</span>`;
            })()}
          </div>
        ` : nothing}

        ${this._expanded || isFullscreen ? html`
          <div class="cp-board-card__detail ${isFullscreen ? 'cp-board-card__detail--fullscreen' : ''}" @click=${(e: Event) => e.stopPropagation()}>
            ${this._renderDetail()}
            ${this._renderSubItems()}
          </div>
        ` : nothing}
      </div>
    `;
  }

  override render() {
    return html`
      ${this._renderCard(false)}
      ${this._fullscreen ? html`
        <div class="cp-board-card-fullscreen__overlay" @click=${this._handleOverlayClick}>
          ${this._renderCard(true)}
        </div>
      ` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-board-card': CpCompanyBoardCard;
  }
}
